/**
 * Server-state hooks of the service dictionaries. Queries opt out of the global error toast — each
 * screen renders its own load-error state (design S1.7) — and every mutation invalidates the slice it
 * changed.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { serviceSetupKeys } from '../api/queryKeys';
import * as api from '../api/serviceSetupApi';

import type {
  AbsenceInput,
  CatalogItemInput,
  CatalogListParams,
  ResourceInput,
  ServiceSettingKey,
  StaffInput,
  StatusUpdateInput,
  WorkOrderGroup,
  WorkTypeInput,
} from '../types/serviceSetupTypes';

const QUIET = { suppressGlobalError: true } as const;

export function useWorkTypes(includeArchived = false) {
  return useQuery({
    queryKey: serviceSetupKeys.workTypes(includeArchived),
    queryFn: () => api.fetchWorkTypes(includeArchived),
    meta: QUIET,
  });
}

export function useCatalog(params: CatalogListParams, enabled = true) {
  return useQuery({
    queryKey: serviceSetupKeys.catalog(params),
    queryFn: () => api.fetchCatalog(params),
    enabled,
    placeholderData: keepPreviousData,
    meta: QUIET,
  });
}

export function useStaff(includeInactive: boolean) {
  return useQuery({
    queryKey: serviceSetupKeys.staff(includeInactive),
    queryFn: () => api.fetchStaff(includeInactive),
    meta: QUIET,
  });
}

export function useLinkableUsers(enabled: boolean) {
  return useQuery({
    queryKey: serviceSetupKeys.linkableUsers(),
    queryFn: api.fetchLinkableUsers,
    enabled,
    meta: QUIET,
  });
}

export function useResources() {
  return useQuery({
    queryKey: serviceSetupKeys.resources(),
    queryFn: api.fetchResources,
    meta: QUIET,
  });
}

export function useResourceLocations(enabled: boolean) {
  return useQuery({
    queryKey: serviceSetupKeys.locations(),
    queryFn: api.fetchResourceLocations,
    enabled,
    meta: QUIET,
  });
}

export function useStatuses() {
  return useQuery({
    queryKey: serviceSetupKeys.statuses(),
    queryFn: api.fetchStatuses,
    meta: QUIET,
  });
}

export function useServiceSettings() {
  return useQuery({
    queryKey: serviceSetupKeys.settings(),
    queryFn: api.fetchServiceSettings,
    meta: QUIET,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return (key: readonly unknown[]) => queryClient.invalidateQueries({ queryKey: key });
}

const SILENT_MUTATION = { meta: QUIET } as const;

export function useSaveWorkType() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string | null; version: number; input: WorkTypeInput }) =>
      v.id === null ? api.createWorkType(v.input) : api.updateWorkType(v.id, v.version, v.input),
    onSuccess: () => invalidate([...serviceSetupKeys.all, 'work-types']),
  });
}

export function useArchiveWorkType() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string; version: number; archived: boolean }) =>
      api.setWorkTypeArchived(v.id, v.version, v.archived),
    onSuccess: () => invalidate([...serviceSetupKeys.all, 'work-types']),
  });
}

export function useSaveCatalogItem() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string | null; version: number; input: CatalogItemInput }) =>
      v.id === null
        ? api.createCatalogItem(v.input)
        : api.updateCatalogItem(v.id, v.version, v.input),
    onSuccess: () => invalidate(serviceSetupKeys.catalogAll()),
  });
}

export function useArchiveCatalogItem() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string; version: number; archived: boolean }) =>
      api.setCatalogItemArchived(v.id, v.version, v.archived),
    onSuccess: () => invalidate(serviceSetupKeys.catalogAll()),
  });
}

export function useSaveStaff() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string | null; version: number; input: StaffInput }) =>
      v.id === null ? api.createStaff(v.input) : api.updateStaff(v.id, v.version, v.input),
    onSuccess: () => {
      void invalidate(serviceSetupKeys.staffAll());
      void invalidate(serviceSetupKeys.linkableUsers());
    },
  });
}

export function useAbsenceMutation() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { staffId: string; absenceId: string | null; input: AbsenceInput | null }) => {
      if (v.input === null) return api.removeAbsence(v.staffId, v.absenceId as string);
      return v.absenceId === null
        ? api.addAbsence(v.staffId, v.input)
        : api.updateAbsence(v.staffId, v.absenceId, v.input);
    },
    onSuccess: () => invalidate(serviceSetupKeys.staffAll()),
  });
}

export function useSaveResource() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string | null; version: number; input: ResourceInput }) =>
      v.id === null ? api.createResource(v.input) : api.updateResource(v.id, v.version, v.input),
    onSuccess: () => invalidate(serviceSetupKeys.resources()),
  });
}

export function useReorderResources() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (ids: string[]) => api.reorderResources(ids),
    onSettled: () => invalidate(serviceSetupKeys.resources()),
  });
}

export function useCreateStatus() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (input: {
      group: WorkOrderGroup;
      nameLv: string;
      nameEn: string;
      timeLimitHours: number | null;
      commentRequired: boolean;
    }) => api.createStatus(input),
    onSuccess: () => invalidate(serviceSetupKeys.statuses()),
  });
}

export function useUpdateStatus() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string; input: StatusUpdateInput }) => api.updateStatus(v.id, v.input),
    onSuccess: () => invalidate(serviceSetupKeys.statuses()),
  });
}

export function useArchiveStatus() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string; version: number; archived: boolean }) =>
      api.setStatusArchived(v.id, v.version, v.archived),
    onSuccess: () => invalidate(serviceSetupKeys.statuses()),
  });
}

export function useMoveStatus() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (v: { id: string; version: number; direction: 'up' | 'down' }) =>
      api.moveStatus(v.id, v.version, v.direction),
    onSuccess: () => invalidate(serviceSetupKeys.statuses()),
  });
}

export function useSaveServiceSettings() {
  const invalidate = useInvalidate();
  return useMutation({
    ...SILENT_MUTATION,
    mutationFn: (values: Partial<Record<ServiceSettingKey, string>>) =>
      api.updateServiceSettings(values),
    onSuccess: () => invalidate(serviceSetupKeys.settings()),
  });
}
